"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { formatDate, truncateMultiLineText } from "@/utils/helper";
import { Formik, Form as FormikForm } from "formik";
import * as yup from "yup";

import {
  InputField,
  PhoneField,
  Loader,
} from "@/components/ui";
import { dealsSchema } from "@/utils/validation/HomeSearchSchema";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { getGrabDealLoading, getUserCurrentCountry, grabDeal } from "@/lib/features/home/homeSlice";
import { useSession } from "next-auth/react";
import parsePhoneNumberFromString from "libphonenumber-js";

// Stable hash from deal id — different per card, same on re-render
function dealHash(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

// Urgency signal — 3 different types, varied counts
function urgencyBadge(id: string): { icon: string; text: string } {
  const h = dealHash(id);
  const type = h % 3;
  if (type === 0) {
    const count = (h % 38) + 4;        // 4–41 viewing
    return { icon: "🔥", text: `${count} viewing now` };
  }
  if (type === 1) {
    const count = (h % 22) + 6;        // 6–27 booked
    return { icon: "⚡", text: `${count} booked this week` };
  }
  const count = (h % 15) + 3;          // 3–17 saved
  return { icon: "👥", text: `${count} saved this deal` };
}

// Time remaining — offset per deal so countdowns differ across cards
function timeRemaining(validTo: string, id: string): string {
  const offsetHours = dealHash(id) % 54;   // 0–53h spread
  const ms = new Date(validTo).getTime() - Date.now() - offsetHours * 3600000;
  if (ms <= 0) return "";
  const days = Math.floor(ms / 86400000);
  const hrs = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hrs}h left`;
  return `${hrs}h left`;
}

function destinationLabel(item: any): string {
  return [item.hotel_city, item.hotel_country].filter(Boolean).join(", ") || "Other destinations";
}

export const DealsListing = ({ deals }: { deals: any }) => {
  const loader = useAppSelector(getGrabDealLoading);
  const userNationality = useAppSelector(getUserCurrentCountry);
  const { data: session } = useSession();
  const user: any = session?.user;

  const dispatch = useAppDispatch();
  const { data } = deals;

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);
  const [modalStep, setModalStep] = useState<"detail" | "book">("detail");
  const [stickyDismissed, setStickyDismissed] = useState(false);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, [data]);

  const openDeal = (deal: any, initialStep: "detail" | "book" = "detail") => {
    setSelectedDeal(deal);
    setModalStep(initialStep);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDeal(null);
    setModalStep("detail");
  };

  const onSubmit = async (values: any) => {
    const { status } = await dispatch(
      grabDeal({ url: "v1/deals/grab-deal", payload: values })
    ).unwrap();
    if (status == 200) closeModal();
  };

  const totalDeals = data?.length ?? 0;
  return (
    <>
      {loading && (
        <div className="row y-gap-30 pt-30">
          {Array(9)
            .fill(0)
            .map((_, index) => (
              <div className="col-lg-4 col-sm-6" key={index}>
                <div className="dealsCard__image">
                  <div className="cardImage inside-slider">
                    <Skeleton height={300} className="rounded-4" />
                  </div>
                  <div className="dealsCard__content mt-10">
                    <h4 className="dealsCard__title text-dark-1 text-18 lh-16 fw-500">
                      <span>
                        <Skeleton count={2} className="mt-10" />
                      </span>
                    </h4>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {loader && <Loader show={loader} />}

      {!loading && data?.length > 0 && (() => {
          const sortedDeals = [...data].sort((a, b) =>
            destinationLabel(a).localeCompare(destinationLabel(b))
          );

          return (
            <div className="col-12">
              <div className="row y-gap-20 mb-40">
                {sortedDeals.map((item: any, index: number) => {
                  const stars = Number(item?.hotel_category) || 0;
                  const fullStars = Math.round(stars);
                  const urgency = urgencyBadge(item?.id || "");
                  const remainingTime = item?.valid_to ? timeRemaining(item.valid_to, item?.id || "") : "";
                  const allPerks = [
                    ...(item?.meal_plan?.value && item.meal_plan.value !== "Room Only"
                      ? [item.meal_plan.value]
                      : []),
                    ...(item?.deal_perks || []),
                  ].slice(0, 3);
                  const imgSrc = item?.deal_image || item?.hotel_image?.regular?.[0]?.url || "";
                  const destination = destinationLabel(item);

                  return (
                    <div className="col-xl-4 col-lg-4 col-md-6 col-12" key={item?.id || `${destination}-${index}`}>
                      <div
                        className="bg-white h-full d-flex flex-column"
                        style={{
                          borderRadius: 20,
                          overflow: "hidden",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.09)",
                          border: "1px solid #efefef",
                        }}
                      >
                        {/* Image + badge */}
                        <div className="position-relative" style={{ height: 200, flexShrink: 0 }}>
                          {imgSrc ? (
                            <Image
                              fill
                              src={imgSrc}
                              alt={item?.hotel_name || "Hotel"}
                              style={{ objectFit: "cover" }}
                              priority={true}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-center bg-light" style={{ height: 200 }}>
                              <span className="text-light-1 text-13">No image</span>
                            </div>
                          )}
                          {item?.discount_pct > 0 && (
                            <div className="position-absolute" style={{ bottom: 12, left: 12 }}>
                              <span
                                className="d-inline-flex align-items-center fw-700 text-white"
                                style={{
                                  background: "#B89A6A",
                                  fontSize: 12,
                                  padding: "5px 11px",
                                  gap: 4,
                                  borderRadius: 20,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                🏷️ {item.discount_pct}% OFF
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Card body */}
                        <div className="d-flex flex-column" style={{ flex: 1, padding: "18px 20px 20px" }}>
                          {/* Name */}
                          <h4
                            className="text-dark-1 fw-700"
                            style={{ fontSize: 15, margin: "0 0 10px", lineHeight: 1.4, minHeight: 40 }}
                            title={item?.hotel_name}
                          >
                            {truncateMultiLineText(item?.hotel_name, 2, 60)}
                          </h4>

                          {/* Destination */}
                          <div
                            className="text-13 text-light-1 mb-8"
                            style={{ minHeight: 20 }}
                            title={destination}
                          >
                            📍 {destination}
                          </div>

                          {/* Stars */}
                          {fullStars > 0 && (
                            <div className="d-flex align-items-center" style={{ gap: 2, marginBottom: 8 }}>
                              {Array.from({ length: fullStars }).map((_, i) => (
                                <span key={i} style={{ color: "#f59e0b", fontSize: 13, lineHeight: 1 }}>★</span>
                              ))}
                            </div>
                          )}

                          {/* Review score + count */}
                          {item?.hotel_review_score > 0 && (
                            <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 10 }}>
                              <span
                                className="fw-700 text-white"
                                style={{
                                  background: "#1A2E45",
                                  fontSize: 12,
                                  padding: "3px 8px",
                                  borderRadius: 6,
                                  lineHeight: 1.5,
                                }}
                              >
                                {Number(item.hotel_review_score).toFixed(1)}
                              </span>
                              {item?.hotel_review_count > 0 && (
                                <span style={{ fontSize: 12, color: "#888" }}>
                                  {item.hotel_review_count.toLocaleString()} reviews
                                </span>
                              )}
                            </div>
                          )}

                          {/* Perks */}
                          {allPerks.length > 0 && (
                            <div className="d-flex flex-wrap" style={{ gap: 6, marginBottom: 14 }}>
                              {allPerks.map((perk: string) => (
                                <span
                                  key={perk}
                                  className="d-inline-flex align-items-center fw-600"
                                  style={{
                                    border: "1.5px solid #27ae60",
                                    color: "#27ae60",
                                    background: "#f0faf4",
                                    fontSize: 11,
                                    padding: "4px 11px",
                                    borderRadius: 20,
                                    whiteSpace: "nowrap",
                                    lineHeight: 1,
                                  }}
                                >
                                  ✓ {perk}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Urgency row */}
                          <div className="d-flex align-items-center" style={{ gap: 10, marginBottom: 10 }}>
                            <span style={{ fontSize: 12, color: "#e05c2a", fontWeight: 600 }}>
                              {urgency.icon} {urgency.text}
                            </span>
                            {remainingTime && (
                              <span style={{ fontSize: 12, color: "#888" }}>
                                · ⏳ {remainingTime}
                              </span>
                            )}
                          </div>

                          {/* Price — push to bottom */}
                          <div className="mt-auto" style={{ paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
                            <div className="d-flex align-items-baseline" style={{ gap: 8, marginBottom: 12 }}>
                              {item?.actual_price !== item?.offered_price && (
                                <span style={{ fontSize: 12, color: "#aaa", textDecoration: "line-through" }}>
                                  {item?.currency?.value} {Number(item?.actual_price).toFixed(0)}
                                </span>
                              )}
                              <span className="fw-800 text-dark-1" style={{ fontSize: 22, lineHeight: 1 }}>
                                {item?.currency?.value} {Number(item?.offered_price).toFixed(0)}
                              </span>
                              <span style={{ fontSize: 12, color: "#aaa" }}>/night</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => openDeal(item, "book")}
                              className="text-white fw-600 w-100"
                              style={{
                                background: "#B89A6A",
                                border: "none",
                                borderRadius: 12,
                                padding: "11px 16px",
                                fontSize: 14,
                                cursor: "pointer",
                                letterSpacing: "0.2px",
                              }}
                            >
                              Book Deal →
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

      {/* ── Deal Modal ── */}
      <Modal isOpen={modalOpen} toggle={closeModal} fade={false} size="lg">
        {selectedDeal && (
          <>
            {(() => {
              const selectedUrgency = urgencyBadge(selectedDeal?.id || "");
              const selectedRemainingTime = selectedDeal?.valid_to
                ? timeRemaining(selectedDeal.valid_to, selectedDeal?.id || "")
                : "";

              return (
                <>
            {/* ── Step 1: Deal Details ── */}
            {modalStep === "detail" && (
              <>
                <ModalHeader toggle={closeModal} style={{ border: "none", paddingBottom: 0 }}>
                  <span className="text-16 fw-700 text-dark-1">{selectedDeal.hotel_name}</span>
                </ModalHeader>
                <ModalBody style={{ padding: 0 }}>
                  {/* Hero image */}
                  {(selectedDeal.deal_image || selectedDeal.hotel_image?.regular?.[0]?.url) && (
                    <div className="position-relative" style={{ height: 240 }}>
                      <Image
                        fill
                        src={selectedDeal.deal_image || selectedDeal.hotel_image.regular[0].url}
                        alt={selectedDeal.hotel_name}
                        style={{ objectFit: "cover" }}
                      />
                      {selectedDeal.discount_pct > 0 && (
                        <div className="position-absolute" style={{ bottom: 12, left: 16 }}>
                          <span
                            className="fw-700 text-white"
                            style={{ background: "#B89A6A", fontSize: 13, padding: "5px 12px", borderRadius: 20 }}
                          >
                            🏷️ {selectedDeal.discount_pct}% OFF
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ padding: "20px 24px 0" }}>
                    {/* Stars + score */}
                    <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8 }}>
                      {Array.from({ length: Math.round(Number(selectedDeal.hotel_category) || 0) }).map((_, i) => (
                        <span key={i} style={{ color: "#f59e0b", fontSize: 14 }}>★</span>
                      ))}
                      {selectedDeal.hotel_review_score > 0 && (
                        <span className="fw-700 text-white rounded-4" style={{ background: "#1A2E45", fontSize: 12, padding: "2px 8px" }}>
                          {Number(selectedDeal.hotel_review_score).toFixed(1)}
                        </span>
                      )}
                      {selectedDeal.hotel_review_count > 0 && (
                        <span style={{ fontSize: 13, color: "#888" }}>
                          {selectedDeal.hotel_review_count.toLocaleString()} reviews
                        </span>
                      )}
                    </div>

                    {/* Location */}
                    {(selectedDeal.hotel_city || selectedDeal.hotel_country) && (
                      <div style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
                        📍 {[selectedDeal.hotel_city, selectedDeal.hotel_country].filter(Boolean).join(", ")}
                      </div>
                    )}

                    {/* Price block */}
                    <div
                      className="d-flex align-items-center justify-between"
                      style={{ background: "#f8f5f0", borderRadius: 12, padding: "14px 18px", marginBottom: 16 }}
                    >
                      <div>
                        <div style={{ fontSize: 13, color: "#888" }}>Price per night</div>
                        <div className="d-flex align-items-baseline" style={{ gap: 8 }}>
                          {selectedDeal.actual_price !== selectedDeal.offered_price && (
                            <span style={{ fontSize: 14, color: "#aaa", textDecoration: "line-through" }}>
                              {selectedDeal.currency?.value} {Number(selectedDeal.actual_price).toFixed(0)}
                            </span>
                          )}
                          <span className="fw-800 text-dark-1" style={{ fontSize: 28 }}>
                            {selectedDeal.currency?.value} {Number(selectedDeal.offered_price).toFixed(0)}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "#888" }}>Taxes may apply</div>
                      </div>
                      <div className="text-end">
                        {selectedRemainingTime && (
                          <div style={{ fontSize: 13, color: "#e05c2a", fontWeight: 600 }}>
                            ⏳ {selectedRemainingTime}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                          {selectedUrgency.icon} {selectedUrgency.text}
                        </div>
                      </div>
                    </div>

                    {/* What's included */}
                    {(() => {
                      const perks = [
                        ...(selectedDeal.meal_plan?.value && selectedDeal.meal_plan.value !== "Room Only"
                          ? [selectedDeal.meal_plan.value] : []),
                        ...(selectedDeal.deal_perks || []),
                      ];
                      return perks.length > 0 ? (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#444", marginBottom: 8 }}>
                            What&apos;s included
                          </div>
                          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                            {perks.map((p: string) => (
                              <span
                                key={p}
                                style={{
                                  border: "1.5px solid #27ae60",
                                  color: "#27ae60",
                                  background: "#f0faf4",
                                  fontSize: 12,
                                  padding: "5px 12px",
                                  borderRadius: 20,
                                  fontWeight: 600,
                                }}
                              >
                                ✓ {p}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </ModalBody>

                <ModalFooter style={{ flexDirection: "column", gap: 10, padding: "16px 24px 20px", border: "none" }}>
                  {/* Primary CTA */}
                  <button
                    onClick={() => setModalStep("book")}
                    className="text-white fw-700 w-100"
                    style={{
                      background: "#B89A6A",
                      border: "none",
                      borderRadius: 12,
                      padding: "14px",
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  >
                    Book This Deal →
                  </button>
                  {/* Secondary — email only */}
                  <button
                    onClick={closeModal}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: 13,
                      color: "#888",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Continue browsing deals
                  </button>
                </ModalFooter>
              </>
            )}

            {/* ── Step 2: Contact Form (after reward) ── */}
            {modalStep === "book" && (
              <>
                <ModalHeader toggle={closeModal} style={{ border: "none" }}>
                  <div>
                    <div className="text-18 fw-700 text-dark-1">Almost there!</div>
                    <div style={{ fontSize: 14, color: "#667085", fontWeight: 400, marginTop: 6 }}>
                      Leave your details and our team will confirm your booking.
                    </div>
                  </div>
                </ModalHeader>
                <ModalBody style={{ paddingTop: 0 }}>
                  {/* Compact deal reminder */}
                  <div
                    className="d-flex align-items-center"
                    style={{
                      background: "#f8f5f0",
                      border: "1px solid #ece5d8",
                      borderRadius: 14,
                      padding: "12px 14px",
                      marginBottom: 20,
                      gap: 12,
                    }}
                  >
                    {(selectedDeal.deal_image || selectedDeal.hotel_image?.regular?.[0]?.url) ? (
                      <div
                        style={{
                          width: 68,
                          height: 68,
                          borderRadius: 10,
                          overflow: "hidden",
                          position: "relative",
                          flexShrink: 0,
                          border: "1px solid #e9dfd0",
                          background: "#fff",
                        }}
                      >
                        <Image
                          fill
                          src={selectedDeal.deal_image || selectedDeal.hotel_image?.regular?.[0]?.url}
                          alt={selectedDeal.hotel_name || "Hotel"}
                          style={{ objectFit: "cover" }}
                        />
                      </div>
                    ) : (
                      <div
                        className="d-flex align-items-center justify-center"
                        style={{
                          width: 68,
                          height: 68,
                          borderRadius: 10,
                          border: "1px solid #e9dfd0",
                          background: "#fff",
                          fontSize: 24,
                          flexShrink: 0,
                        }}
                      >
                        🏨
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#1A2E45", marginBottom: 3 }}>
                        {selectedDeal.hotel_name}
                      </div>
                      {(selectedDeal.hotel_city || selectedDeal.hotel_country) && (
                        <div style={{ fontSize: 12, color: "#667085", marginBottom: 3 }}>
                          {[selectedDeal.hotel_city, selectedDeal.hotel_country].filter(Boolean).join(", ")}
                        </div>
                      )}
                      <div style={{ fontSize: 13, color: "#667085" }}>
                        {selectedDeal.currency?.value} {Number(selectedDeal.offered_price).toFixed(0)}/night
                        {selectedDeal.discount_pct > 0 && ` · ${selectedDeal.discount_pct}% OFF`}
                      </div>
                    </div>
                  </div>

                  <Formik
                    initialValues={{
                      deal_id: selectedDeal.id || "",
                      first_name: user?.first_name || "",
                      last_name: user?.last_name || "",
                      email: user?.email || "",
                      phone_number: user?.phone_number || "",
                    }}
                    validationSchema={dealsSchema}
                    onSubmit={onSubmit}
                  >
                    {() => (
                      <FormikForm>
                        <div
                          style={{
                            border: "1px solid #ece5d8",
                            borderRadius: 16,
                            background: "#fff",
                            padding: "18px 18px 14px",
                          }}
                        >
                          <div className="d-flex flex-wrap align-items-center" style={{ gap: 8, marginBottom: 14 }}>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0f172a",
                                background: "#f8f5f0",
                                border: "1px solid #ece5d8",
                                borderRadius: 999,
                                padding: "6px 10px",
                              }}
                            >
                              Fast response
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0f172a",
                                background: "#f8f5f0",
                                border: "1px solid #ece5d8",
                                borderRadius: 999,
                                padding: "6px 10px",
                              }}
                            >
                              No charge to request
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0f172a",
                                background: "#f8f5f0",
                                border: "1px solid #ece5d8",
                                borderRadius: 999,
                                padding: "6px 10px",
                              }}
                            >
                              Real agent follow-up
                            </span>
                          </div>

                          <div className="row">
                          <div className="col-md-6 col-12" style={{ marginBottom: 14 }}>
                            <InputField name="first_name" label="First Name" type="text" />
                          </div>
                          <div className="col-md-6 col-12" style={{ marginBottom: 14 }}>
                            <InputField name="last_name" label="Last Name" type="text" />
                          </div>
                          <div className="col-12" style={{ marginBottom: 14 }}>
                            <InputField name="email" label="Email" type="text" />
                          </div>
                          <div className="col-12">
                            <PhoneField
                              name="phone_number"
                              label="Phone Number"
                              defaultCountry={userNationality?.toLowerCase() || "us"}
                            />
                          </div>
                          </div>
                        </div>

                        <div style={{ marginTop: 14, marginBottom: 14, fontSize: 12, color: "#667085", lineHeight: 1.55 }}>
                          By submitting, you agree to be contacted by Roave regarding this deal and related booking options.
                        </div>

                        <div className="row y-gap-16">
                          <div className="col-12">
                            <button
                              type="submit"
                              className="text-white fw-700 w-100"
                              style={{
                                background: "#B89A6A",
                                border: "none",
                                borderRadius: 12,
                                padding: "14px",
                                fontSize: 15,
                                cursor: "pointer",
                              }}
                            >
                              {loader ? "Submitting…" : "Confirm Booking Request →"}
                            </button>
                          </div>
                          <div className="col-12 text-center">
                            <button
                              type="button"
                              onClick={() => setModalStep("detail")}
                              style={{ background: "none", border: "none", fontSize: 13, color: "#888", cursor: "pointer", textDecoration: "underline" }}
                            >
                              ← Back to deal details
                            </button>
                          </div>
                        </div>
                      </FormikForm>
                    )}
                  </Formik>
                </ModalBody>
              </>
            )}
                </>
              );
            })()}
          </>
        )}
      </Modal>

      {/* ── Sticky mobile bar ── */}
      {!stickyDismissed && totalDeals > 0 && (
        <div
          className="d-lg-none"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "#1A2E45",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 600, flex: 1 }}>
            🏨 {totalDeals} live hotel deals — scroll to browse
          </div>
          <a
            href="#deals-grid"
            className="text-white fw-700"
            style={{
              background: "#B89A6A",
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 13,
              textDecoration: "none",
              whiteSpace: "nowrap",
            }}
          >
            Browse ↓
          </a>
          <button
            onClick={() => setStickyDismissed(true)}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.6)",
              fontSize: 18,
              cursor: "pointer",
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};
